import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import EventsTimeFilter from '../components/EventsTimeFilter';
import HeatmapVisualization from '../components/HeatmapVisualization';
import { RefreshCw } from 'lucide-react';
import Icon from '../../../shared/components/Icon';

const HeatmapPage = () => {
  const params = useParams();
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem("selectedTeam") || '');
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [urls, setUrls] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [timeFilter, setTimeFilter] = useState(null);
  const [selectedTimePreset, setSelectedTimePreset] = useState('30d');
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingUris, setLoadingUris] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [stripQueryParams, setStripQueryParams] = useState(false);

  // Initialize with last 30 days
  useEffect(() => {
    if (selectedTeam && !timeFilter) {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setTimeFilter({
        type: 'preset',
        preset: '30d',
        from: thirtyDaysAgo.toISOString(),
        to: now.toISOString()
      });
    }
  }, [selectedTeam, timeFilter]);

  // Fetch URIs when team, time, or URL grouping changes
  useEffect(() => {
    if (selectedTeam && timeFilter) {
      fetchUris();
    }
  }, [selectedTeam, timeFilter, stripQueryParams]);

  // Fetch heatmap when URL is selected
  useEffect(() => {
    if (selectedUrl && timeFilter && selectedTeam) {
      fetchHeatmapData();
    }
  }, [selectedUrl, timeFilter, selectedTeam]);

  const fetchUris = async () => {
    setLoadingUris(true);
    setError(null);
    try {
      const teamId = selectedTeam || localStorage.getItem("selectedTeam");
      if (!teamId) {
        setError('Team ID not found');
        setLoadingUris(false);
        return;
      }

      const params = new URLSearchParams({
        team_id: teamId
      });
      
      if (selectedWebsite) {
        params.set('website_id', selectedWebsite);
      }

      if (timeFilter?.from) {
        params.set('from', timeFilter.from);
      }

      if (timeFilter?.to) {
        params.set('to', timeFilter.to);
      }

      if (stripQueryParams) {
        params.set('strip_query_params', 'true');
      }

      const response = await axiosInstance.get(`/events/heatmap/uris?${params.toString()}`);
      
      if (response.data.success && response.data.urls) {
        setUrls(response.data.urls);
        // Auto-select first URL if none selected
        if (!selectedUrl && response.data.urls.length > 0) {
          setSelectedUrl(response.data.urls[0].url);
        }
      }
    } catch (err) {
      console.error('Error fetching URIs:', err);
      setError('Failed to load page URLs');
    } finally {
      setLoadingUris(false);
    }
  };

  const fetchHeatmapData = async () => {
    setLoading(true);
    setError(null);
    try {
      const teamId = selectedTeam || localStorage.getItem("selectedTeam");
      if (!teamId) {
        setError('Team ID not found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        team_id: teamId,
        url: selectedUrl,
        grid_size: 40
      });
      
      if (selectedWebsite) {
        params.set('website_id', selectedWebsite);
      }
      
      if (timeFilter?.from) {
        params.set('from', timeFilter.from);
      }

      if (timeFilter?.to) {
        params.set('to', timeFilter.to);
      }

      if (stripQueryParams) {
        params.set('strip_query_params', 'true');
      }

      const response = await axiosInstance.get(`/events/heatmap?${params.toString()}`);
      
      if (response.data.success) {
        setHeatmapData(response.data);
        setLastRefreshTime(new Date());
      }
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      setError('Failed to load heatmap data');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    setSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
  };

  const handleRefresh = () => {
    if (selectedUrl) {
      fetchHeatmapData();
    }
    fetchUris();
  };

  const formatLastRefresh = () => {
    if (!lastRefreshTime) return '';
    const now = new Date();
    const diffMs = now - lastRefreshTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  };

  if (!selectedTeam) {
    return (
      <div className="flex-grow overflow-y-auto p-6">
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-[#caa968] rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-[#1d0c46] font-medium" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Please select a team to view heatmaps.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden relative flex-col">
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <div className="bg-white" style={{ fontFamily: 'Raleway, sans-serif' }}>
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Heatmap
              </h1>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
                title="Refresh heatmap"
              >
                <RefreshCw className="h-4 w-4" />
                {lastRefreshTime && (
                  <span className="text-xs">Updated {formatLastRefresh()}</span>
                )}
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap border-t border-gray-200">
            {/* Left: Time Filter */}
            <div className="min-w-[520px]">
              <EventsTimeFilter 
                onTimeFilterChange={handleTimeFilterChange} 
                selectedPreset={selectedTimePreset} 
              />
            </div>

            {/* Right: Page URL Selector + grouping toggle */}
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              {/* Group by path toggle */}
              <label
                className="flex items-center gap-1.5 cursor-pointer select-none"
                title="Merge pages that share the same path but differ only in query parameters (e.g. UTM variants)"
              >
                <div
                  onClick={() => setStripQueryParams(v => !v)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    stripQueryParams ? 'bg-[#4b57aa]' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    stripQueryParams ? 'translate-x-4' : 'translate-x-1'
                  }`} />
                </div>
                <span className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Group by path
                </span>
              </label>

              <div className="flex items-center gap-2">
                <Icon name="globe" size={18} color="#475569" />
                <span className="text-sm text-[#475569]">Page:</span>
                <select
                  value={selectedUrl || ''}
                  onChange={(e) => setSelectedUrl(e.target.value)}
                  disabled={loadingUris || urls.length === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-[#0f172a] min-w-[300px] max-w-md"
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {loadingUris && <option>Loading pages...</option>}
                  {!loadingUris && urls.length === 0 && <option>No pages found</option>}
                  {!loadingUris && urls.length > 0 && (
                    <>
                      <option value="">Select a page</option>
                      {urls.map((item) => (
                        <option key={item.url} value={item.url}>
                          {item.url} ({item.count} events)
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Heatmap Visualization - single scroll lives inside here */}
        <div className="flex-1 min-h-0 p-6 flex flex-col">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {!selectedUrl && !loading && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
              <p className="text-sm">Please select a page URL to view its heatmap.</p>
            </div>
          )}

          {selectedUrl && (
            <HeatmapVisualization
              data={heatmapData}
              loading={loading}
              pageUrl={selectedUrl}
              teamId={selectedTeam}
              websiteId={selectedWebsite}
              timeFilter={timeFilter}
              stripQueryParams={stripQueryParams}
              className="flex-1 min-h-0 flex flex-col"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HeatmapPage;
